
import {
  AIAnalysis,
  Application,
  CandidateDetail,
  CandidateDetailsResponse,
  Job,
} from "../types";
import { buildStatusHistory } from "./candidateStatus";
import {
  calculateDuration,
  flattenSkills,
  formatDate,
  formatExperience,
  getNumber,
  toArray,
  toText,
} from "./formatters";

export interface MappedCandidateData {
  id: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  position: string;
  department: string;
  experience: string;
  currentCompany: string;
  currentDesignation: string;
  currentSalary: string;
  expectedSalary: string;
  noticePeriod: string;
  linkedin: string | null;
  portfolio: string | null;
  source: string;
  appliedDate: string;
  statusHistory: Array<{
    status: string;
    date: string;
    time: string;
  }>;
}

export interface CandidateAllocationDetails {
  status?: "AVAILABLE" | "ALLOCATED" | "BLACKLISTED" | string;
  allocationStatus: "AVAILABLE" | "ALLOCATED" | "BLACKLISTED" | string;
  allocationTeamId: string | null;
  allocationClaimedById: string | null;
  allocationApplicationId: string | null;
  allocationStartedAt: string | null;
  isAllocatedToCurrentTeam: boolean;
  isAllocatedToAnotherTeam: boolean;
  isAllocatedToCurrentApplication: boolean;
  canManageApplication: boolean;
  isBlacklisted: boolean;
  blacklistedAt: string | null;
  blacklistExpiresAt: string | null;
}

type ApplicationWithAllocationPermission = Application & {
  canManageApplication?: boolean;
};

type CandidateDetailsResponseWithAllocation =
  CandidateDetailsResponse & {
    allocation?: CandidateAllocationDetails | null;
    applications?: ApplicationWithAllocationPermission[];
  };

const mapSkillList = (value: unknown): string[] =>
  toArray<unknown>(value)
    .map((item: unknown) => {
      if (typeof item === "string") {
        return item;
      }

      if (item && typeof item === "object") {
        const objectItem = item as {
          skill?: unknown;
          name?: unknown;
        };

        return objectItem.skill ?? objectItem.name ?? "";
      }

      return "";
    })
    .filter((item) => item !== "")
    .map(String);

export const mapCandidateData = (
  candidate: CandidateDetail | null,
  job: Job | null,
  application: Application | null
): MappedCandidateData => ({
  id: toText(candidate?.id ?? application?.id),
  name: toText(candidate?.name),
  email: toText(candidate?.email),
  phone: toText(candidate?.phone),
  location: toText(candidate?.currentLocation),
  position: toText(job?.title),
  department: toText(job?.department),

  // Candidate's selected experience from application.
  // Do not calculate this from resume experience.
  experience: formatExperience(candidate?.totalExperienceMonths),

  currentCompany: toText(candidate?.currentCompany),
  currentDesignation: toText(candidate?.currentDesignation),
  currentSalary: toText(candidate?.currentSalary),
  expectedSalary: toText(candidate?.expectedSalary),
  noticePeriod: toText(candidate?.noticePeriod),
  linkedin: candidate?.linkedinUrl ?? null,
  portfolio: candidate?.portfolioUrl ?? null,
  source: toText(application?.source),
  appliedDate: formatDate(application?.createdAt),
  statusHistory: buildStatusHistory(application, formatDate),
});

export const mapAIAnalysis = (
  apiAI: Partial<AIAnalysis> | null | undefined
): AIAnalysis => {
  const matchedSkills = mapSkillList(apiAI?.matchedSkills);
  const missingSkills = mapSkillList(apiAI?.missingSkills);

  const experience = toArray<unknown>(
    apiAI?.resumeAnalysis?.experience
  ).map((item: unknown) => {
    const exp = (item ?? {}) as Record<string, unknown>;

    const startDate =
      exp.startDate ??
      exp.start ??
      exp.from ??
      null;

    const endDate =
      exp.endDate ??
      exp.end ??
      exp.to ??
      null;

    const currentlyWorking =
      exp.currentlyWorking === true ||
      String(exp.currentlyWorking ?? "").toLowerCase() === "true" ||
      String(exp.currentlyWorking ?? "").toLowerCase() === "yes";

    const calculatedDuration = calculateDuration(
      startDate,
      endDate,
      currentlyWorking
    );

    const parsedDuration = exp.duration ?? exp.period;

    const duration =
      parsedDuration &&
        String(parsedDuration).trim() !== "" &&
        String(parsedDuration).trim().toUpperCase() !== "N/A"
        ? String(parsedDuration)
        : calculatedDuration;

    return {
      company: toText(exp.company ?? exp.companyName),
      role: toText(exp.role ?? exp.designation ?? exp.title),
      startDate: startDate ? String(startDate) : null,
      endDate: endDate ? String(endDate) : null,
      currentlyWorking,
      duration,
      highlights: toArray<unknown>(
        exp.highlights ?? exp.responsibilities
      ).map(String),
    };
  });

  const education = toArray<unknown>(
    apiAI?.resumeAnalysis?.education
  ).map((item: unknown) => {
    const edu = (item ?? {}) as Record<string, unknown>;

    const startYear = edu.startYear ?? edu.start ?? null;
    const endYear =
      edu.endYear ??
      edu.end ??
      edu.passingYear ??
      null;

    const year = endYear || startYear || edu.year || null;

    return {
      degree: toText(
        edu.degree ?? edu.qualification ?? edu.educationLevel
      ),
      institution: toText(
        edu.institution || edu.university
      ),
      year: toText(year),
      score: toText(
        edu.score ?? edu.cgpa ?? edu.percentage
      ),
    };
  });

  const certifications = toArray<unknown>(
    apiAI?.resumeAnalysis?.certifications
  ).map((item: unknown) => {
    const certification =
      (item ?? {}) as Record<string, unknown>;

    return {
      name: toText(certification.name),
      issuer: toText(certification.issuer),
      credentialId: toText(certification.credentialId),
      credentialUrl: toText(certification.credentialUrl),
      issuedDate: toText(certification.issuedDate),
      expiryDate: toText(certification.expiryDate),
      neverExpires:
        certification.neverExpires === true ||
        String(certification.neverExpires ?? "").toLowerCase() ===
        "true",
    };
  });

  return {
    atsScore: getNumber(apiAI?.atsScore),
    resumeScore: getNumber(apiAI?.resumeScore),
    videoScore: getNumber(apiAI?.videoScore),

    jdMatchScore: getNumber(apiAI?.jobMatchScore),

    recommendation: apiAI?.recommendation
      ? String(apiAI.recommendation)
      : null,

    confidence: getNumber(apiAI?.confidence),

    strengths: toArray<unknown>(apiAI?.strengths).map(String),
    weaknesses: toArray<unknown>(apiAI?.weaknesses).map(String),

    skillGaps: [
      ...matchedSkills.map((skill) => ({
        skill,
        hasExperience: true,
      })),
      ...missingSkills.map((skill) => ({
        skill,
        hasExperience: false,
      })),
    ],

    resumeAnalysis: {
      skills: flattenSkills(apiAI?.resumeAnalysis?.skills),
      experience,
      education,
      certifications,
    },

    videoAnalysis: {
      confidence: getNumber(apiAI?.videoAnalysis?.confidence),
      communication: getNumber(
        apiAI?.videoAnalysis?.communication
      ),
      fluency: getNumber(apiAI?.videoAnalysis?.fluency),
      professionalism: getNumber(
        apiAI?.videoAnalysis?.professionalism
      ),
      eyeContact: getNumber(apiAI?.videoAnalysis?.eyeContact),
      voiceClarity: getNumber(
        apiAI?.videoAnalysis?.voiceClarity
      ),
      grammar: getNumber(apiAI?.videoAnalysis?.grammar),
      bodyLanguage: getNumber(
        apiAI?.videoAnalysis?.bodyLanguage
      ),
      energy: getNumber(apiAI?.videoAnalysis?.energy),
      keyPoints: toArray<unknown>(
        apiAI?.videoAnalysis?.keyPoints
      ).map(String),
    },

    interviewRecommendation: {
      proceed: Boolean(
        apiAI?.interviewRecommendation?.proceed
      ),
      round: toText(
        apiAI?.interviewRecommendation?.nextRound ??
        apiAI?.interviewRecommendation?.round
      ),
      focusAreas: toArray<unknown>(
        apiAI?.interviewRecommendation?.focusAreas
      ).map(String),
      suggestedQuestions: toArray<unknown>(
        apiAI?.interviewRecommendation?.suggestedQuestions
      ).map(String),
    },
  };
};

export const mapCandidateDetails = (
  data: CandidateDetailsResponse | null
) => {
  const candidate =
    data?.candidateData?.candidate ?? null;

  const job =
    data?.candidateData?.job ?? null;

  const application =
    data?.candidateData?.application ?? null;

  // Preserve SH-26 allocation metadata and application-level
  // permissions while shared API types are updated separately.
  const responseWithAllocation =
    data as CandidateDetailsResponseWithAllocation | null;

  return {
    candidate,
    job,

    resume: data?.candidateData?.resume ?? null,
    video: data?.candidateData?.video ?? null,
    application,

    allocation: responseWithAllocation?.allocation ?? null,

    // Keep the per-application permission returned by the backend.
    // Do not derive permissions from candidate-level allocation alone.
    applications: responseWithAllocation?.applications ?? [],

    candidateData: mapCandidateData(
      candidate,
      job,
      application
    ),

    aiAnalysis: mapAIAnalysis(data?.aiAnalysis),
  };
};
